import { ExploreTestID } from "@/constants/TestID";
import { ActionBar, FAIconName } from "@/ui/core";
import { joinTestIDs } from "@/utils/string.util";

const PreTreatmentTestID = ExploreTestID.GroupClassSeriesPreTreatmentDetails;
const SensitiveTestID = ExploreTestID.GroupClassSeriesSensitiveDetails;

const ActionBarMessages = {
  PreTreatment:
    "This group will appear on your schedule with a link to join after your treatment starts",
  RecommendedGroupsMessage:
    "Groups are a core component of treatment where you’ll get most of the education and skills for your recovery toolkit.",
  RecommendedGroupTitle: "We recommend registering for another group.",
  SensitivePatientUser:
    "Only you and your provider team can see your registration for this group.",
  SensitiveSupportUser:
    "For this particular group, the patient will need to register via their own account.",
} as const;

const PreTreatmentDetailsActionBar = () => (
  <ActionBar
    iconName={FAIconName.InfoCircle}
    message={ActionBarMessages.PreTreatment}
    size="small"
    testID={joinTestIDs(PreTreatmentTestID, "pre-treatment-action-bar")}
    variant="warning"
  />
);

const SensitiveDetailsSupportUserActionBar = () => (
  <ActionBar
    iconName={FAIconName.InfoCircle}
    message={ActionBarMessages.SensitiveSupportUser}
    size="small"
    testID={joinTestIDs(SensitiveTestID, "support")}
    variant="primary"
  />
);

const SensitiveDetailsPatientUserActionBar = () => (
  <ActionBar
    iconName={FAIconName.EyeSlash}
    message={ActionBarMessages.SensitivePatientUser}
    size="small"
    testID={joinTestIDs(SensitiveTestID, "patient")}
    variant="warning"
  />
);

interface SensitiveDetailsActionBarProps {
  isAppUserSupport: boolean;
  isSensitive?: boolean;
  // true when a support user is viewing a linked patient (the selected user is not the
  // logged-in support user) — only then do we show the support "register on own account"
  // banner. Defaults to true so callers without a selected-user concept (v1 details) keep
  // the original support+sensitive behavior.
  isViewingLinkedPatient?: boolean;
}

const SensitiveDetailsActionBar = ({
  isAppUserSupport,
  isSensitive,
  isViewingLinkedPatient = true,
}: SensitiveDetailsActionBarProps) => {
  if (!isSensitive) return null;
  // patient viewing their own registration: only they and their provider team can see it.
  // (a support viewing their OWN sensitive registration gets no banner)
  if (!isAppUserSupport) return <SensitiveDetailsPatientUserActionBar />;
  // support viewing a linked patient: the patient must register on their own account
  if (isViewingLinkedPatient) return <SensitiveDetailsSupportUserActionBar />;
  return null;
};

export {
  ActionBarMessages,
  PreTreatmentDetailsActionBar,
  SensitiveDetailsActionBar,
};
export type { SensitiveDetailsActionBarProps };
